/* Shuddh Swad sticky add-to-cart bar (snippets/ss-sticky-atc.liquid). Owner: WP-PDP (v3).
 * - Proxies its click to #ProductSubmitButton-{sectionId}, so Dawn's product-form.js does the add.
 * - Shows whenever the buy row (quantity + Add to cart + Buy now, .product-form__qty-atc-row) is OFF screen,
 *   above or below the viewport (mobile-first: on arrival the row sits below the fold, so the bar shows at once).
 *   Hides while the buy row is in view, while the combo builder's card (rows + buy block) is on screen (one primary
 *   button per screen), near the footer, while a drawer/modal is open (Dawn sets body.overflow-hidden*) and
 *   while a text field has focus.
 *   Note: product-form, its form and .product-form__buttons are display:contents (no box), so the row is
 *   the element to observe; the button itself is the fallback.
 * - Follows variant changes (PUB_SUB variantChange + change on the form's input[name="id"]).
 * - Toggles body.ss-sticky-atc-visible and --ss-sticky-atc-height on <html> for chat launchers.
 * - v3: the offer line follows the selected variant ("offer" in the variants JSON: a multi-pack is ONE cart item). */
if (!customElements.get('ss-sticky-atc')) {
  /* The whole builder card (size control, flavour rows and its buy block): while the shopper is choosing, the
     bar's single-pack Add to cart must not sit under the rows (one primary per screen). */
  const SS_COMBO_BUY = 'ss-combo-builder .ss-combo__card, ss-combo-builder .ss-combo__buy, ss-combo-builder [data-ss-combo-atc]';

  customElements.define(
    'ss-sticky-atc',
    class SsStickyAtc extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onPackClick = this.onPackClick.bind(this);
        this.onDocChange = this.onDocChange.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.measure = this.measure.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-ss-sticky-button]');
        if (!this.sectionId || !this.button || !('IntersectionObserver' in window)) return;

        this.variants = this.readVariants();
        this.state = { offscreen: false, footer: false, overlay: false, typing: false };
        this.zones = new Set(); /* combo builder cards / buy blocks currently on screen */
        this.visible = false;
        this.observed = null;

        this.removeAttribute('hidden');
        this.setVisible(false);

        this.button.addEventListener('click', this.onClick);
        this.querySelector('[data-ss-sticky-pack]')?.addEventListener('click', this.onPackClick);
        document.addEventListener('change', this.onDocChange);
        document.addEventListener('focusin', this.onFocusIn);
        document.addEventListener('focusout', this.onFocusOut);
        window.addEventListener('resize', this.measure, { passive: true });

        /* Buy row: top margin = sticky header, bottom margin = the bar's height (65px) plus a little, so the bar
           leaves as soon as the row comes out from behind it (Add to cart is then fully visible) and never sits on
           top of a visible Buy now. */
        this.io = new IntersectionObserver((entries) => this.onIntersect(entries), {
          rootMargin: '-64px 0px -72px 0px',
        });
        /* Footer and the combo builder's buy block: the bar steps aside as soon as any part is on screen. */
        this.zoneIo = new IntersectionObserver((entries) => this.onZone(entries));
        this.footer = document.querySelector('.shopify-section-group-footer-group, footer');
        if (this.footer) this.zoneIo.observe(this.footer);
        document.querySelectorAll(SS_COMBO_BUY).forEach((el) => this.zoneIo.observe(el));
        this.observeTarget();

        this.bodyObserver = new MutationObserver(() => this.checkOverlay());
        this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        this.checkOverlay();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || data.sectionId !== this.sectionId) return;
            this.observeTarget();
            this.renderVariant(data.variant ? data.variant.id : null);
          });
        }

        const idInput = this.mainForm && this.mainForm.querySelector('input[name="id"]');
        if (idInput && idInput.value) this.renderVariant(idInput.value);
      }

      disconnectedCallback() {
        if (!this.state) return;
        this.button.removeEventListener('click', this.onClick);
        this.querySelector('[data-ss-sticky-pack]')?.removeEventListener('click', this.onPackClick);
        document.removeEventListener('change', this.onDocChange);
        document.removeEventListener('focusin', this.onFocusIn);
        document.removeEventListener('focusout', this.onFocusOut);
        window.removeEventListener('resize', this.measure);
        this.io && this.io.disconnect();
        this.zoneIo && this.zoneIo.disconnect();
        this.bodyObserver && this.bodyObserver.disconnect();
        this.buttonObserver && this.buttonObserver.disconnect();
        this.unsubscribe && this.unsubscribe();
        document.body.classList.remove('ss-sticky-atc-visible');
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get mainForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      get productInfo() {
        const id = window.CSS && CSS.escape ? CSS.escape(this.sectionId) : this.sectionId;
        return document.querySelector(`product-info[data-section="${id}"]`);
      }

      readVariants() {
        try {
          return JSON.parse(this.querySelector('[data-ss-sticky-variants]').textContent) || [];
        } catch (e) {
          return [];
        }
      }

      /* ---------- visibility ---------- */

      /* The buy row (a real grid box holding quantity, Add to cart and Buy now), else the main button. Never
         .product-form__buttons: it is display:contents and so never intersects. */
      observeTarget() {
        const main = this.mainButton;
        const info = this.productInfo;
        const target =
          (main && main.closest('.product-form__qty-atc-row')) ||
          main ||
          (info && info.querySelector('.product-form__qty-atc-row'));
        if (target === this.observed) return;
        if (this.observed) this.io.unobserve(this.observed);
        this.observed = target;
        if (target) this.io.observe(target);
        this.watchMainButton();
      }

      onIntersect(entries) {
        entries.forEach((entry) => {
          if (entry.target === this.observed) this.state.offscreen = !entry.isIntersecting;
        });
        this.update();
      }

      onZone(entries) {
        entries.forEach((entry) => {
          if (entry.target === this.footer) {
            this.state.footer = entry.isIntersecting;
          } else if (entry.isIntersecting) {
            this.zones.add(entry.target);
          } else {
            this.zones.delete(entry.target);
          }
        });
        this.update();
      }

      checkOverlay() {
        this.state.overlay =
          /(^|\s)overflow-hidden/.test(document.body.className) ||
          !!document.querySelector('cart-drawer.active, quick-add-modal[open]');
        this.update();
      }

      isTextField(el) {
        if (!el || this.contains(el)) return false;
        if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
        return el.tagName === 'INPUT' && !/^(button|submit|checkbox|radio|range|hidden|image|reset)$/.test(el.type);
      }

      onFocusIn(event) {
        if (!this.isTextField(event.target)) return;
        this.state.typing = true;
        this.update();
      }

      onFocusOut() {
        if (!this.state.typing) return;
        /* Wait for focus to land: moving between two fields must not flash the bar. */
        requestAnimationFrame(() => {
          this.state.typing = this.isTextField(document.activeElement);
          this.update();
        });
      }

      update() {
        const s = this.state;
        const show =
          s.offscreen && !s.footer && !s.overlay && !s.typing && this.zones.size === 0 && !!this.observed;
        if (show !== this.visible) this.setVisible(show);
      }

      setVisible(show) {
        this.visible = show;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('ss-sticky-atc-visible', show);
        if (show) this.measure();
      }

      measure() {
        this.fitMeta();
        if (!this.visible) return;
        document.documentElement.style.setProperty('--ss-sticky-atc-height', `${this.offsetHeight}px`);
      }

      /* Price, struck MRP and the pack pill share one line. When they don't fit (narrow phones, 5 Pack prices),
         drop the struck MRP rather than wrapping the bar to an extra line. Layout exists while hidden. */
      fitMeta() {
        const meta = this.querySelector('.ss-sticky-atc__meta');
        if (!meta) return;
        meta.classList.remove('is-tight');
        if (meta.scrollWidth > meta.clientWidth + 1) meta.classList.add('is-tight');
      }

      /* ---------- main button mirroring ---------- */

      watchMainButton() {
        const main = this.mainButton;
        if (main === this.watched) return;
        this.buttonObserver && this.buttonObserver.disconnect();
        this.watched = main;
        if (!main) return;
        this.buttonObserver = new MutationObserver(() => this.syncFromMain());
        this.buttonObserver.observe(main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
        this.syncFromMain();
      }

      syncFromMain() {
        const main = this.mainButton;
        if (!main) return;
        const loading = main.classList.contains('loading');
        const disabled = main.hasAttribute('disabled');
        const mainLabel = main.querySelector('span');
        const label = this.querySelector('[data-ss-sticky-button-label]');

        this.button.disabled = disabled;
        this.button.classList.toggle('loading', loading);
        this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
        const spinner = this.button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
        if (label && mainLabel && mainLabel.textContent.trim()) label.textContent = mainLabel.textContent.trim();

        if (this.pending && !loading) {
          this.pending = false;
          const productForm = this.mainForm && this.mainForm.closest('product-form');
          const wrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
          if (wrapper && !wrapper.hasAttribute('hidden')) this.scrollToMain(wrapper);
        }
      }

      onClick(event) {
        event.preventDefault();
        const main = this.mainButton;
        if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
        this.pending = true;
        main.click();
      }

      scrollToMain(el) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      }

      onPackClick() {
        const info = this.productInfo;
        const selects = info && info.querySelector('variant-selects');
        if (!selects) return;
        const field = selects.querySelector('input:checked') || selects.querySelector('select, input');
        this.scrollToMain(selects);
        if (field) field.focus({ preventScroll: true });
      }

      /* ---------- variant rendering ---------- */

      /* Offer line for the variant: server-written per variant ("2+ items: …" for a 1 Pack, "+1 item: …" for a
         3 Pack, 5 Pack or combo, which are ONE cart item each). */
      syncOffer(variant) {
        const line = this.querySelector('[data-ss-sticky-offer]');
        if (!line || !variant || !variant.offer) return;
        if (line.textContent !== variant.offer) line.textContent = variant.offer;
      }

      onDocChange(event) {
        const t = event.target;
        if (!t || t.name !== 'id') return;
        const form = this.mainForm;
        if (form && (form.contains(t) || t.form === form)) this.renderVariant(t.value);
      }

      renderVariant(id) {
        const variant = id ? this.variants.find((v) => String(v.id) === String(id)) : null;
        const priceEl = this.querySelector('[data-ss-sticky-price]');
        const compareWrap = this.querySelector('[data-ss-sticky-compare-wrap]');
        const compareEl = this.querySelector('[data-ss-sticky-compare]');
        const packLabel = this.querySelector('[data-ss-sticky-pack-label]');

        if (!variant) {
          const label = this.querySelector('[data-ss-sticky-button-label]');
          if (label) label.textContent = this.dataset.labelUnavailable || '';
          this.button.disabled = true;
          return;
        }

        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
        const onSale = variant.compare > variant.price;
        if (compareWrap) compareWrap.hidden = !onSale;
        if (compareEl) compareEl.textContent = onSale ? this.formatMoney(variant.compare) : '';

        if (packLabel) packLabel.textContent = variant.pack || variant.title;
        this.syncOffer(variant);

        this.renderImage(variant.img);
        this.measure();

        /* The main button's state is authoritative (quantity rules etc.); fall back to the JSON. */
        if (this.mainButton) {
          this.syncFromMain();
        } else {
          const label = this.querySelector('[data-ss-sticky-button-label]');
          this.button.disabled = !variant.available;
          if (label) label.textContent = variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
        }
      }

      renderImage(src) {
        if (!src) return;
        const box = this.querySelector('.ss-sticky-atc__media');
        if (!box) return;
        let img = box.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'ss-sticky-atc__img';
          img.alt = '';
          img.width = 48;
          img.height = 48;
          box.appendChild(img);
        }
        const abs = new URL(src, window.location.href).href;
        if (img.src === abs) return;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
      }

      formatMoney(cents) {
        const format = (this.dataset.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
        const pattern = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(pattern);
        const amount = Number(cents) / 100;
        const build = (decimals, thousands, decimal) => {
          const parts = amount.toFixed(decimals).split('.');
          const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          /* Same as Liquid money_without_trailing_zeros: drop ".00" */
          return parts[1] && Number(parts[1]) !== 0 ? whole + decimal + parts[1] : whole;
        };
        let value;
        switch (match ? match[1] : 'amount') {
          case 'amount_no_decimals':
            value = build(0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = build(2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = build(0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = build(2, "'", '.');
            break;
          default:
            value = build(2, ',', '.');
        }
        return match ? format.replace(pattern, value) : value;
      }
    }
  );
}
